import React, { useState, useEffect } from "react";
import GlobalSelect from "@/components/ui/GlobalSelectItem";
import { FormSectionProps } from "./type";
import { ALL_BANKS } from "@/constant/paymentMethodConstant";
import { COUNTRIES } from "@/constant";

export type TGlobalSelectOption = {
  id: string;
  value?: number | string;
  title: string;
};

interface BankSelectionSectionProps extends FormSectionProps {
  selectedCountry: string;
  onCountryChange: (country: string, countryName?: string) => void;
}

const BankSelectionSection: React.FC<BankSelectionSectionProps> = ({
  formData,
  selectedCountry,
  onFormDataChange,
  onCountryChange,
}) => {
  // State for selected options
  const [selectedCountryOption, setSelectedCountryOption] =
    useState<TGlobalSelectOption | null>(() => {
      const currentCountry = COUNTRIES.find(
        (country) =>
          country.id.toUpperCase() === selectedCountry.toUpperCase() ||
          country.title === selectedCountry
      );
      return currentCountry
        ? {
            id: currentCountry.id.toUpperCase(),
            title: currentCountry.title,
            value: currentCountry.id.toUpperCase(),
          }
        : null;
    });

  const [selectedBankOption, setSelectedBankOption] =
    useState<TGlobalSelectOption | null>(() => {
      const currentBank = formData.details.bankName;
      if (currentBank) {
        const matchingBank = ALL_BANKS.find(
          (bank) => bank.title === currentBank
        );
        if (matchingBank) {
          return {
            id: matchingBank.id,
            title: matchingBank.title,
            value: matchingBank.value,
          };
        }
      }
      return null;
    });

  // Update selected country when selectedCountry prop changes
  useEffect(() => {
    const countryOption = COUNTRIES.find(
      (country) =>
        country.id.toUpperCase() === selectedCountry.toUpperCase() ||
        country.title === selectedCountry
    );
    if (countryOption) {
      setSelectedCountryOption({
        id: countryOption.id.toUpperCase(),
        title: countryOption.title,
        value: countryOption.id.toUpperCase(),
      });
    }
  }, [selectedCountry]);

  const getFilteredBanks = (): TGlobalSelectOption[] => {
    return ALL_BANKS.filter((bank) => bank.country === selectedCountry).map(
      (bank) => ({
        id: bank.id,
        title: bank.title,
        value: bank.value,
      })
    );
  };

  // Convert COUNTRIES to TGlobalSelectOption format
  const countryOptions: TGlobalSelectOption[] = COUNTRIES.map((country) => ({
    id: country.id.toUpperCase(),
    title: country.title,
    value: country.id.toUpperCase(),
  }));

  const handleCountrySelect = (option: TGlobalSelectOption | null) => {
    if (option) {
      setSelectedCountryOption(option);

      // Find the corresponding country code for the selected country
      const selectedCountryData = COUNTRIES.find(
        (country) => country.id.toUpperCase() === option.id.toUpperCase()
      );
      if (selectedCountryData) {
        // Use the country code (like 'IN', 'US', 'UK') for filtering banks
        const countryCode = selectedCountryData.id.toUpperCase();
        onCountryChange(countryCode, selectedCountryData.title);
      } else {
        onCountryChange(String(option.id), option.title);
      }

      // Clear bank selection when country changes
      setSelectedBankOption(null);
      onFormDataChange("bankName", "", true);
    }
  };

  const handleBankSelect = (option: TGlobalSelectOption | null) => {
    if (option) {
      setSelectedBankOption(option);
      onFormDataChange("bankName", option.title, true);

      // Auto-set country based on bank selection
      const selectedBankData = ALL_BANKS.find((bank) => bank.id === option.id);
      if (selectedBankData) {
        onCountryChange(selectedBankData.country);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Country Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Country
        </label>
        <GlobalSelect
          position="relative"
          label=""
          options={countryOptions}
          selectedOption={selectedCountryOption}
          onSelect={handleCountrySelect}
          placeholder="Select Country"
          showOtherInputField={true}
        />
      </div>

      {/* Bank Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Bank Name
        </label>
        <GlobalSelect
          position="relative"
          label=""
          options={getFilteredBanks()}
          selectedOption={selectedBankOption}
          onSelect={handleBankSelect}
          placeholder="Select Bank"
          showOtherInputField={true}
        />
      </div>
    </div>
  );
};

export default BankSelectionSection;
